// Kind-detection feedback banner for the verbal-authoring draft review.
//
// The load-bearing UX bet for "the operator gets better at speaking
// recipes over time" — always renders, even on high confidence, and
// shows the LLM's reasoning string verbatim so the operator learns
// which spoken phrases drove the inferred kind. One-click override via
// a small select; the override updates `draft.kind` in place and does
// NOT re-run extraction (planning doc §7).
//
// Plan reference: `docs/planning/mfg_recipe_verbal_authoring.md` §7.

import type { MfgRecipeKind } from "@/lib/manufacturing/types";
import type { DictatedDraftDetectedKind } from "@/lib/manufacturing/types/recipeDictation";

import { MFG_RECIPE_KIND_OPTIONS } from "@/lib/manufacturing/recipeKinds";

const KIND_OPTIONS = MFG_RECIPE_KIND_OPTIONS.map((o) => ({
  value: o.id,
  label: o.label.split(" (")[0] ?? o.label,
}));

const selectClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100";

export function ManufacturingDictatedRecipeKindFeedback({
  currentKind,
  detectedKind,
  onKindChange,
}: {
  currentKind: MfgRecipeKind;
  detectedKind: DictatedDraftDetectedKind;
  onKindChange: (kind: MfgRecipeKind) => void;
}) {
  const isHighConfidence = detectedKind.confidence === "high";
  const overridden = currentKind !== detectedKind.value;

  const containerTone = isHighConfidence
    ? "border-slate-200 bg-slate-50 dark:border-zinc-700 dark:bg-zinc-800/40"
    : "border-amber-200 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/35";
  const headerTone = isHighConfidence
    ? "text-slate-700 dark:text-zinc-300"
    : "text-amber-800 dark:text-amber-200";

  return (
    <div
      className={`rounded-lg border px-3 py-2 ${containerTone}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-3">
        <div className={`text-xs font-semibold uppercase tracking-wide ${headerTone}`}>
          Detected kind: <span className="font-bold">{detectedKind.value}</span>
          <span className="ml-2 font-normal lowercase opacity-75">
            ({detectedKind.confidence} confidence)
          </span>
          {overridden ? (
            <span className="ml-2 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-normal normal-case text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200">
              Overridden → {currentKind}
            </span>
          ) : null}
        </div>
        <label className="text-xs text-gray-600 dark:text-zinc-300">
          <span className="mr-1">Kind</span>
          <select
            className={selectClass}
            value={currentKind}
            onChange={(e) => onKindChange(e.target.value as MfgRecipeKind)}
          >
            {KIND_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {detectedKind.reasoning ? (
        <div className="mt-1 text-xs text-gray-700 dark:text-zinc-300">
          {detectedKind.reasoning}
        </div>
      ) : (
        <div className="mt-1 text-xs italic text-gray-500 dark:text-zinc-400">
          No reasoning provided — try dictating the kind explicitly next time (e.g.
          "this is an option recipe").
        </div>
      )}
    </div>
  );
}
